import { StudyPageContentSchema, type StudyPageContentPatch } from '@nava/backoffice-bff-contracts';
import { backofficeFetch } from '@/services/backoffice-api';
import {
  ExperienceProfileListSchema,
  ExperienceDefaultsSchema,
  ExperienceBindingSchema,
  ExperienceProfileSchema,
  type ExperienceProfileInput,
  type ExperienceProfileUpdate,
  type ExperienceDefaults,
} from '@nava/backoffice-bff-contracts';
async function request(org: string, path: string, method = 'GET', body?: unknown) {
  const response = await backofficeFetch(
    `/organizations/${encodeURIComponent(org)}/participant-experience${path}`,
    {
      method,
      ...(body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    }
  );
  const data: unknown = await response.json();
  if (!response.ok)
    throw new Error(
      typeof data === 'object' && data && 'error' in data ? String(data.error) : 'Request failed'
    );
  return data;
}
export async function loadExperienceProfiles(org: string) {
  const items = [];
  let offset = 0;
  for (;;) {
    const page = ExperienceProfileListSchema.parse(
      await request(org, `/profiles?limit=100&offset=${offset}`)
    );
    items.push(...page.items);
    offset += page.items.length;
    if (offset >= page.page.total || page.items.length === 0) return items;
  }
}
export const loadExperienceDefaults = async (org: string) =>
  ExperienceDefaultsSchema.parse(await request(org, '/defaults'));
export const saveExperienceDefaults = async (org: string, input: ExperienceDefaults) =>
  ExperienceDefaultsSchema.parse(await request(org, '/defaults', 'PUT', input));
export const loadExperienceBinding = async (org: string, study: string) =>
  ExperienceBindingSchema.parse(await request(org, `/studies/${encodeURIComponent(study)}`));
export const saveExperienceBinding = async (org: string, study: string, profileId: string | null) =>
  ExperienceBindingSchema.parse(
    await request(org, `/studies/${encodeURIComponent(study)}`, 'PUT', { profileId })
  );
export const createExperienceProfile = async (
  org: string,
  input: ExperienceProfileInput,
  source?: string
) =>
  ExperienceProfileSchema.parse(
    await request(
      org,
      `/profiles${source ? `/${encodeURIComponent(source)}/clones` : ''}`,
      'POST',
      input
    )
  );
export const updateExperienceProfile = async (
  org: string,
  id: string,
  input: ExperienceProfileUpdate
) =>
  ExperienceProfileSchema.parse(
    await request(org, `/profiles/${encodeURIComponent(id)}`, 'PUT', input)
  );
export const archiveExperienceProfile = async (org: string, id: string) =>
  request(org, `/profiles/${encodeURIComponent(id)}`, 'DELETE');

export const publishExperience = async (org: string, study: string) =>
  request(org, `/studies/${encodeURIComponent(study)}/publications`, 'POST', {});

export const loadStudyPageContent = async (org: string, study: string) =>
  StudyPageContentSchema.parse(await request(org, `/studies/${encodeURIComponent(study)}/content`));
export const saveStudyPageContent = async (
  org: string,
  study: string,
  input: StudyPageContentPatch
) =>
  StudyPageContentSchema.parse(
    await request(org, `/studies/${encodeURIComponent(study)}/content`, 'PATCH', input)
  );
